import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { isSSEMessage } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { createReconnectableStream } from './createReconnectableStream';
import { createReconnectionMetrics } from './reconnect-action';
import type { ChatReconnectableStreamOptions, SSEStreamChunk } from './reconnectable-stream';

const RECONNECT_ABORT_REASON_CHAT_UNMOUNTED = 'chat-unmounted';

/**
 * Creates a reconnectable stream pre-wired with Rovo chat conventions:
 * - Reconnects via `assistanceService.reconnectToMessageStream`
 * - Extracts SSE event IDs from `ParsedSSEMessage` chunks
 * - Fires `rovoChatReconnection` analytics on each retry
 * - Tracks RECONNECT_TO_ACTIVE_STREAM experience (start/succeed/fail/abort)
 * - Tracks reconnection performance metrics (timeToConnect, timeToResume)
 * - Updates conversation traces with retry status
 * - Pauses reconnection when chat is unmounted
 *
 * Callers pass hooks for site-specific concerns (dead air tracking,
 * inactivity watchdogs, ID alignment, MAU events).
 */
export function createChatReconnectableStream({
	generator,
	conversationId,
	abortController,
	assistanceService,
	sendAnalyticsEvent,
	experienceTracker,
	getMessageId,
	canReconnect,
	updateCurrentConversation,
	getIsChatMounted,
	analyticsAttributes,
	onBeforeFirstReconnect: callerOnBeforeFirstReconnect,
	onReconnecting: callerOnReconnecting,
	onReconnected: callerOnReconnected,
	onFirstChunkAfterReconnect: callerOnFirstChunkAfterReconnect,
}: ChatReconnectableStreamOptions): AsyncGenerator<SSEStreamChunk> {
	let totalAttempts = 0;
	let perfMetrics: ReturnType<typeof createReconnectionMetrics> | undefined;

	return createReconnectableStream<SSEStreamChunk>({
		initialGenerator: generator,

		canReconnect: () => !!getMessageId() && canReconnect(),

		getReconnectGenerator: (lastEventId) => {
			const messageId = getMessageId();
			if (!messageId) {
				return Promise.reject(new Error('Cannot reconnect: messageId is not available'));
			}
			return assistanceService
				.reconnectToMessageStream({
					conversationId,
					messageId,
					lastEventId,
					controller: abortController,
				})
				.then((r: { generator: AsyncGenerator<SSEStreamChunk> }) => r.generator);
		},

		getEventId: (chunk) => (isSSEMessage(chunk) && chunk.id ? chunk.id : undefined),

		onBeforeFirstReconnect: () => {
			perfMetrics = createReconnectionMetrics({
				isDeepResearchEnabled: analyticsAttributes?.isDeepResearch as boolean | undefined,
				thinkDeeperEnabled: analyticsAttributes?.thinkDeeperEnabled as boolean | undefined,
				fastModeEnabled: analyticsAttributes?.fastModeEnabled as boolean | undefined,
			});
			perfMetrics.timeToConnectToStreamMetrics.start();
			experienceTracker.start({
				name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
				id: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
				attributes: {
					conversationId,
					messageId: getMessageId(),
					...analyticsAttributes,
				},
			});
			callerOnBeforeFirstReconnect?.();
		},

		onReconnecting: (attempt, maxAttempts, lastEventId) => {
			totalAttempts = attempt;
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'retrying',
				actionSubject: 'rovoChatReconnection',
				attributes: {
					conversationId,
					messageId: getMessageId(),
					attempt,
					maxAttempts,
					hasLastEventId: !!lastEventId,
					...analyticsAttributes,
				},
			});
			updateCurrentConversation({
				traces: [
					{
						messageContent: `Connection lost. Retrying... (${attempt}/${maxAttempts})`,
						status: 'loading',
					},
				],
			});
			callerOnReconnecting?.(attempt, maxAttempts);
		},

		onReconnected: () => {
			perfMetrics?.timeToConnectToStreamMetrics.stop();
			perfMetrics?.timeToResumeStreamMetrics.start();
			callerOnReconnected?.();
		},

		onFirstChunkAfterReconnect: () => {
			perfMetrics?.timeToResumeStreamMetrics.stop();
			callerOnFirstChunkAfterReconnect?.();
		},

		onStreamCompleted: () => {
			experienceTracker.succeed({
				name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
				attributes: {
					totalAttempts,
					conversationId,
					messageId: getMessageId(),
					...analyticsAttributes,
				},
			});
		},

		onStreamFailed: (error) => {
			perfMetrics?.timeToConnectToStreamMetrics.cancel();
			perfMetrics?.timeToResumeStreamMetrics.cancel();

			if (
				(error instanceof Error && error.name === 'AbortError') ||
				abortController.signal.aborted
			) {
				experienceTracker.abort({
					name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
					reason: 'Aborted',
				});
			} else {
				experienceTracker.fail({
					name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
					error: error instanceof Error ? error : new Error(String(error)),
					attributes: {
						totalAttempts,
						conversationId,
						messageId: getMessageId(),
						...analyticsAttributes,
					},
				});
			}
		},

		shouldContinueRetrying: getIsChatMounted
			? () => {
					if (!getIsChatMounted()) {
						abortController.abort(RECONNECT_ABORT_REASON_CHAT_UNMOUNTED);
						return false;
					}
					return true;
				}
			: undefined,

		signal: abortController.signal,
	});
}
